// SF3 — discount depth bands, and SF6 — lift no promotion explains.
//
// The depth view shows REPEATS SEPARATELY rather than averaging them, because
// the effect of the same discount fades as customers learn to wait for it, and
// an average across runs hides exactly that decay. Margin leads: a band with
// the best lift and negative margin is a loss.
//
// Spikes sit here rather than on their own page because they are episodes of
// the same kind as promotions — a bounded departure from expected — differing
// only in that their cause is inferred rather than declared.
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { FiAlertTriangle, FiInfo } from 'react-icons/fi';

import AuthContext from '../../../../context/AuthContext';
import local from '../Promotions.module.css';
import { getDepth, getSpikes } from '../services/promotionsApi';

const inr = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 });

const Money = ({ value }) => {
  if (value === null || value === undefined) return <span className={local.blank}>—</span>;
  return (
    <span className={value >= 0 ? local.paid : local.lost}>
      {value >= 0 ? '+' : '−'}₹{inr.format(Math.abs(Math.round(value)))}
    </span>
  );
};

const DepthAndSpikes = () => {
  const authCtx = useContext(AuthContext);
  const token = authCtx?.token;
  const [depth, setDepth] = useState(null);
  const [spikes, setSpikes] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true); setError(null);
    try {
      const [d, s] = await Promise.all([getDepth(token), getSpikes(token)]);
      setDepth(d); setSpikes(s);
    } catch (err) { setError(err); }
    finally { setLoading(false); }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  return (
    <>
      <section className={local.card}>
        <div className={local.cardHead}>
          <h2 className={local.cardTitle}>What each discount depth has returned</h2>
        </div>
        <p className={local.muted}>
          Repeats are shown separately, not averaged — the same discount returns
          less each time customers learn to wait for it, and an average hides
          that. Margin leads, so the deepest lift can still be the worst band.
        </p>

        {error && (
          <div className={`${local.banner} ${local.bannerError}`}>
            <FiAlertTriangle className={local.bannerIcon} />
            <div><strong>Could not load depth analysis</strong><div>{error.message}</div></div>
          </div>
        )}

        {!error && (depth?.products || []).length === 0 && (
          <div className={local.emptyCell}>
            {loading ? 'Loading…' : 'No promotion episodes with a measurable depth yet.'}
          </div>
        )}

        {(depth?.products || []).map((product) => (
          <div key={product.sku} className={local.indexGroup}>
            <h3 className={local.indexGroupTitle}>{product.sku}</h3>
            <div className={local.tableScroll}>
              <table className={local.table}>
                <thead>
                  <tr>
                    <th>Depth</th>
                    <th className={local.numeric}>Runs</th>
                    <th className={local.numeric}>Median margin</th>
                    <th>Each run, in order</th>
                  </tr>
                </thead>
                <tbody>
                  {product.bands.map((band) => (
                    <tr key={band.band}>
                      <td>{band.band}</td>
                      <td className={`${local.numeric} ${local.context}`}>{band.runs}</td>
                      <td className={local.numeric}>
                        {band.status === 'measured'
                          ? <Money value={band.median_margin_effect} />
                          : <span className={local.blank} title={band.reason}>{band.status}</span>}
                      </td>
                      <td>
                        {band.by_run ? (
                          <span className={local.fade}>
                            {band.by_run.map((run) => (
                              <span key={run.start} className={local.fadeStep}
                                title={`${run.start}: ${run.units_effect} units`}>
                                {run.margin_effect >= 0 ? '+' : '−'}
                                ₹{inr.format(Math.abs(Math.round(run.margin_effect || 0)))}
                              </span>
                            ))}
                          </span>
                        ) : <span className={local.context}>{band.reason}</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </section>

      <section className={local.card}>
        <div className={local.cardHead}>
          <h2 className={local.cardTitle}>Lift no promotion explains</h2>
          {spikes && <span className={local.muted}>detected at {spikes.sigma}σ</span>}
        </div>
        <p className={local.muted}>
          Demand that jumped with nothing behind it. These days are held out of
          every promotion's comparison window — a viral week must not become the
          baseline the next quarter's promotions are judged against.
        </p>

        {(spikes?.products || []).length === 0 ? (
          <div className={local.emptyCell}>
            {loading ? 'Loading…' : 'No unexplained lift detected.'}
          </div>
        ) : (
          <div className={local.tableScroll}>
            <table className={local.table}>
              <thead>
                <tr>
                  <th>Product</th>
                  <th>When</th>
                  <th className={local.numeric}>Peak</th>
                  <th className={local.numeric}>Units above expected</th>
                  <th>Cause</th>
                </tr>
              </thead>
              <tbody>
                {spikes.products.flatMap((product) =>
                  product.spikes.map((spike) => (
                    <tr key={`${product.sku}-${spike.start}`}>
                      <td>{product.sku}</td>
                      <td className={local.context}>{spike.start} → {spike.end}</td>
                      <td className={local.numeric}>×{spike.peak_ratio}</td>
                      <td className={local.numeric}>+{spike.units_above_expected}</td>
                      <td>
                        <span className={local.spikeChip}>
                          {spike.cause} ({spike.cause_basis})
                        </span>
                      </td>
                    </tr>
                  )))}
              </tbody>
            </table>
          </div>
        )}

        {spikes?.notes?.length > 0 && (
          <ul className={local.noteList}>
            {spikes.notes.slice(0, 4).map((note) => (
              <li key={note}><FiInfo className={local.noteIcon} />{note}</li>
            ))}
          </ul>
        )}
      </section>
    </>
  );
};

export default DepthAndSpikes;
